import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { Job, JobStatus } from '@/types';
import { useMutation } from '@/utils/api';

export const ExportFormat = {
  PDF: 'pdf',
  ZIP: 'zip',
} as const;

export type ExportFormatType = (typeof ExportFormat)[keyof typeof ExportFormat];

// Constants for polling behavior (mirrors useAsyncExports)
const INITIAL_POLL_INTERVAL = 5000; // 5 seconds
const MAX_RETRIES = 60; // 5 minutes total (60 * 5s = 300s)
const MAX_POLL_INTERVAL = 30000; // 30 seconds maximum

export interface ReportConfig {
  title: string;
  client_name: string;
  target: string;
  start_date: string;
  end_date: string;
  report_date: string;
  draft: boolean;
  retest: boolean;
  version: string;
}

export interface ValidateReportParams {
  risk_keys?: string[];
  status_filter: string[];
  customer_email: string;
  executive_summary_path?: string;
  narratives_path?: string;
  group_by?: string;
}

export interface ValidateReportError {
  type: string;
  risk_name: string;
  risk_key: string;
}

export interface ValidateReportResult {
  valid: boolean;
  errors: ValidateReportError[];
}

export interface ExportReportParams {
  risk_keys?: string[];
  status_filter: string[];
  config: ReportConfig;
  shared_output: boolean;
  customer_email: string;
  executive_summary_path?: string;
  narratives_path?: string;
  appendix_path?: string;
  export_format?: ExportFormatType;
  group_by?: string;
}

/**
 * Hook to validate a PDF report before generation.
 * POSTs to /validate-report and returns any errors (missing definitions, narratives).
 */
export function useValidateReport() {
  const axios = useAxios();

  return useMutation<ValidateReportResult, Error, ValidateReportParams>({
    defaultErrorMessage: 'Failed to validate report',
    mutationFn: async (params: ValidateReportParams) => {
      const response = await axios.post<ValidateReportResult>(
        '/validate-report',
        params
      );
      return response.data;
    },
  });
}

/**
 * Hook to generate a PDF report as an async job.
 * POSTs to /export/report, polls for job completion, then triggers download.
 * Mirrors the polling pattern from useAsyncExports.
 */
export function useExportReport() {
  const axios = useAxios();

  return useMutation<Job, Error, ExportReportParams>({
    defaultErrorMessage: 'Failed to generate report',
    mutationFn: async (params: ExportReportParams) => {
      const formatLabel =
        params.export_format === ExportFormat.ZIP ? 'ZIP' : 'PDF';

      const toastId = toast.loading(`Generating ${formatLabel} report...`, {
        duration: Infinity,
      });

      try {
        // Start the report generation job
        const exportResponse = await axios.post<Job>('/export/report', params);

        if (!exportResponse?.data?.key) {
          throw new Error('Invalid export response');
        }

        // Poll for job completion with exponential backoff
        let retryCount = 0;
        let completedJob: Job | undefined;

        while (retryCount < MAX_RETRIES) {
          const jobResponse = await axios.get('/my', {
            params: { key: exportResponse.data.key },
          });

          if (!jobResponse?.data?.jobs?.[0]) {
            throw new Error('Invalid job response');
          }

          const job = jobResponse.data.jobs[0];
          const status = job.status.slice(0, 2);

          if (status === JobStatus.Pass) {
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getMy('file'),
            });
            const config = job.config as Record<string, string> | undefined;
            const outputPath = config?.output ?? job.dns;
            const fileRes = await axios.get<{ url: string }>('/file', {
              params: { name: outputPath },
            });
            (job as Job & { _pdfUrl?: string })._pdfUrl = fileRes.data.url;
            completedJob = job;
            break;
          }

          if (status === JobStatus.Fail) {
            throw new Error(job.message || `${formatLabel} generation failed`);
          }

          if (status !== JobStatus.Running && status !== JobStatus.Queued) {
            throw new Error(`Unknown job status: ${status}`);
          }

          const interval = Math.min(
            INITIAL_POLL_INTERVAL * (1 + Math.floor(retryCount / 2)),
            MAX_POLL_INTERVAL
          );
          await new Promise(resolve => setTimeout(resolve, interval));
          retryCount++;
        }

        if (!completedJob) {
          throw new Error(
            `${formatLabel} generation timed out after 5 minutes. Please try again.`
          );
        }

        const pdfUrl = (completedJob as Job & { _pdfUrl?: string })._pdfUrl;
        toast.dismiss(toastId);
        toast(`${formatLabel} report generated`, {
          duration: 15000,
          action: pdfUrl
            ? {
                label: formatLabel === 'ZIP' ? 'Download ZIP' : 'Download PDF',
                onClick: () => window.open(pdfUrl, '_blank'),
              }
            : undefined,
          actionButtonStyle: {
            background: 'rgb(var(--chariot-brand-primary))',
            color: 'white',
            border: 'none',
          },
        });
        return completedJob;
      } catch (error) {
        toast.dismiss(toastId);
        toast.error(
          error instanceof Error
            ? error.message
            : `Failed to generate ${formatLabel} report`,
          { duration: 8000 }
        );
        throw error;
      }
    },
  });
}
